import React, { ReactNode } from 'react';
import classNames, { Argument } from 'classnames';
import { XMarkIcon } from '@heroicons/react/24/solid';

import Spinner from './Spinner';
import MandatorySymbol from './MandatorySymbol';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  center?: boolean;
  label?: ReactNode;
  value?: string | number;
  loading?: boolean;
  onClear?: () => void;
  type?: React.HTMLInputTypeAttribute;
  disabled?: boolean;
  required?: boolean;
  error?: React.ReactNode | boolean;
  className?: Argument;
  icon?: React.ReactNode;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  'data-testid'?: string;
  inputClassName?: Argument;
  labelClassName?: Argument;
  iconClassName?: Argument;
  errorClassname?: Argument;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  center,
  label,
  onChange,
  onClear,
  onKeyDown,
  value,
  className,
  loading,
  icon,
  required,
  disabled,
  type,
  error,
  'data-testid': dataTestId,
  inputClassName,
  labelClassName,
  iconClassName,
  errorClassname,
  ...rest
}, ref) => {
  const id = React.useId();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const refToUse = ref ?? inputRef;
  const hideErrorMessage = !error || typeof error === 'boolean';

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && 'current' in refToUse) {
      refToUse.current.blur();
    }
    onKeyDown?.(event);
  };

  const displayIcon = Boolean(icon || loading || onClear);
  const hasValue = value && String(value).length > 0;

  return (
    <div
      className={classNames(
        'relative z-0 group rounded-input',
        className,
      )}
    >
      <input
        id={id}
        ref={refToUse}
        type={type}
        value={value}
        placeholder=" "
        onChange={onChange}
        required={required}
        disabled={disabled}
        className={classNames('input focus:outline-none peer bg-input-bg', {
          'border-danger-default focus:border-danger-default': error,
          'kiosk:pr-20': Boolean(icon),
          'text-center': center,
          'pt-8': !label,
        }, inputClassName)}
        data-testid={dataTestId}
        autoComplete="off"
        {...rest}
        onKeyDown={handleKeyDown}
      />
      {label && (
        <>
          <label className={classNames('input-label kiosk:peer-focus:-translate-y-6', { '!text-danger-default': error }, hasValue &&  'kiosk:-translate-y-6', labelClassName)} htmlFor={id}>
            {required && <MandatorySymbol className="!leading-none" />}
            {label}
          </label>
        </>
      )}
      {displayIcon && (
        <div className={classNames('absolute top-5 right-5 kiosk:h-full kiosk:top-0 kiosk:right-10', iconClassName)}>
          {loading && <Spinner className="loadingIcon kiosk:h-full" />}
          {!loading && (
            <>
              <div
                onClick={onClear}
                data-testid="input-clear-btn"
                // eslint-disable-next-line max-len
                className="size-7 -mr-1.5 -mt-1.5 kiosk:w-16 kiosk:h-16 kiosk:mt-0 kiosk:-mr-4 kiosk:h-full cursor-pointer flex justify-between items-center clearIcon"
              >
                {Boolean(onClear) && hasValue && (
                  <XMarkIcon className="m-auto size-3.5 kiosk:w-8 kiosk:h-8 kiosk:h-full" />
                )}
                {!hasValue && icon}
              </div>
            </>
          )}
        </div>
      )}
      {!hideErrorMessage && (
        <span
          className={classNames(
            'text-danger-default px-1 text-xs kiosk:text-xl block !mt-2.5 kiosk:mt-5',
            { 'text-center': center },
            errorClassname
          )}
          data-testid="error-text"
        >
          {error}
        </span>
      )}
    </div>
  );
});

export default Input;
